// Small display helpers for the Video Editing dashboard.

// Seconds → m:ss (or h:mm:ss). Null/unknown durations render as an em dash so a
// just-uploaded clip (Drive hasn't processed it yet) reads as "unknown", not "0:00".
export function formatDuration(totalSec: number | null | undefined): string {
  if (totalSec == null || !isFinite(totalSec)) return '—';
  const sec = Math.max(0, Math.round(totalSec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Turn a stored taxonomy value like `broll_only` into `broll only` for display,
// without losing the stored value. Shared by everything that shows a clip-label
// enum — the clip label editor and the project's product type.
export function humanize(value: string): string {
  return value.replace(/_/g, ' ');
}

// Bytes → human-readable size. Null renders as an em dash.
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}

/**
 * What to call a clip nobody has named — the source file plus the range it covers.
 *
 * Every AI-labelled clip has a null `name`, so without this the card simply has no title line.
 * The range is part of it because several clips carved out of one take would otherwise all read
 * the same, and telling them apart is exactly what you're doing when you look at them together.
 */
export function defaultBrollName(
  clip: { fileName?: string | null; fileId?: string; inSec?: number | null; outSec?: number | null },
): string {
  const base = clip.fileName || clip.fileId || 'clip';
  if (clip.inSec == null || clip.outSec == null) return base;
  return `${base} (${clip.inSec.toFixed(1)}–${clip.outSec.toFixed(1)}s)`;
}
